import differenceInSeconds from 'date-fns/differenceInSeconds';
import debounce from 'lodash/debounce';
import mixpanel from 'mixpanel-browser';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { Box, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import QuoteBackground from '../components/assets/QuoteBackground';
import Nav from '../components/Nav';
import StepAboutYourPet from '../components/QuoteForm/StepAboutYourPet';
// import StepAdditionalAboutPet from '../components/QuoteForm/StepAdditionalAboutPet';
import StepAboutYourself from '../components/QuoteForm/StepAboutYourself';
import StepCoverage from '../components/QuoteForm/StepCoverage';
import StepDogOrCat from '../components/QuoteForm/StepDogOrCat';
import StepsNavigation from '../components/StepsNavigation';
import StepWizard, { StepWizardChildProps } from '../components/StepWizard';

interface QuoteFormData {
  name?: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  nav: {
    paddingTop: 27,
    paddingBottom: 20,
    [theme.breakpoints.up('sm')]: {
      backgroundColor: 'white',
    },
  },
  background: {
    position: 'fixed',
    zIndex: -1,
    left: 0,
    bottom: 0,

    [theme.breakpoints.up('md')]: {
      left: '55%',
      transform: 'translateX(-47%)',
    },
  },
  stepsNavigation: {
    position: 'absolute',
    bottom: 33,
    left: 0,
    right: 0,
    marginLeft: (theme.overrides!.MuiContainer!.root as any).paddingLeft,
    marginRight: (theme.overrides!.MuiContainer!.root as any).paddingRight,

    [theme.breakpoints.up('md')]: {
      bottom: 23,
    },
  },
  stepWizard: {
    marginTop: 30,
    marginBottom: 30,

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  contentContainer: {
    position: 'relative', // 'relative' so we can position the step navigation
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 135,

    [theme.breakpoints.up('md')]: {
      paddingBottom: 113,
    },
  },
}));

const Quote = () => {
  const classes = useStyles({});
  const router = useRouter();

  const [data, setData] = useState<QuoteFormData>({});
  const [sessionStartTime] = useState(new Date());

  useEffect(() => {
    mixpanel.track('Start session on form');
  }, []);

  const handleUpdate = (newData) => {
    const mergedData = { ...data, ...newData };

    setData(mergedData);
  };

  const handleNext = (step) => {
    mixpanel.track(`Step ${step}`, { ...data });
  };

  const handleSubmit = () => {
    const sessionTime = differenceInSeconds(new Date(), sessionStartTime);
    mixpanel.track(`Step 4`, { ...data });
    mixpanel.track('Session time on form', { sessionTime });

    const queryString = Object.keys(data)
      .map((key) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
      })
      .join('&');

    router.push(`/quotes?${queryString}`);
  };

  return (
    <>
      <Head>
        <title>Get Insurance Quotes For Your Pets - Breezy</title>
        <meta
          name="description"
          content="Fill one simple form to receive quotes from all major pet insurance providers."
        />
      </Head>

      <div className={classes.root}>
        <Nav className={classes.nav} />
        <QuoteBackground className={classes.background} />
        <Box
          // position="relative"
          display="flex"
          flex={1}
          style={{ overflowX: 'hidden' }}
        >
          <Container className={classes.contentContainer}>
            <StepWizard
              nav={(props: StepWizardChildProps) => (
                <StepsNavigation
                  className={classes.stepsNavigation}
                  currentStep={props.currentStep}
                  totalSteps={props.totalSteps}
                  onNext={props.nextStep}
                  onBack={props.previousStep}
                  onSubmit={handleSubmit}
                  valid={props.valid}
                />
              )}
              onNext={handleNext}
              className={classes.stepWizard}
            >
              <StepDogOrCat onUpdate={debounce(handleUpdate, 200)} />
              <StepAboutYourPet
                data={data}
                onUpdate={debounce(handleUpdate, 200)}
              />
              {/* <StepAdditionalAboutPet
                data={data}
                onUpdate={debounce(handleUpdate, 200)}
              /> */}
              <StepAboutYourself onUpdate={handleUpdate} />
              <StepCoverage onUpdate={debounce(handleUpdate, 200)} />
            </StepWizard>
          </Container>
        </Box>
      </div>
      <style jsx global>
        {`
          body {
            background-color: #f7f7fa !important;
          }
        `}
      </style>
    </>
  );
};

export default Quote;
